import { FunctionComponent } from 'react';
import { useGetOrderQuery } from '@/api/v2/order';
import {
    Box,
    Container,
    Link,
    Stack,
    SvgIcon,
    Typography,
} from '@mui/material';
import { formatStarjackDate } from '@/utils/time';
import { CustomerOrderCard } from '@/pageContents/customers/details/CustomerOrderCard';
import { useGetCustomerQuery } from '@/api/v2/customer';
import { OrderDetailsKeycardsTable } from '@/pageContents/customers/orderDetails/OrderDetailsKeycardsTable';
import { OrderDetailsShopProductsTable } from '@/pageContents/customers/orderDetails/OrderDetailsShopProductsTable';
import { OrderDetailsTicketsTable } from '@/pageContents/customers/orderDetails/OrderDetailsTicketsTable';
import { OrderDetailsVouchersTable } from '@/pageContents/customers/orderDetails/OrderDetailsVouchersTable';
import { OrderDetailsMailsTable } from '@/pageContents/customers/orderDetails/OrderDetailsMailsTable';
import ArrowLeftIcon from '@heroicons/react/24/solid/ArrowLeftIcon';
import { LoadingContainer } from '@starjack/shared-components';

type OrderDetailsContentProps = {
    customerId: string;
    orderId: string;
};

export const OrderDetailsContent: FunctionComponent<
    OrderDetailsContentProps
> = ({ orderId, customerId }) => {
    const { data: order, isLoading: orderLoading } = useGetOrderQuery({
        orderId,
        customerId,
    });
    const { data: customer, isLoading: customerLoading } = useGetCustomerQuery({
        customerId,
    });

    if (customerLoading || orderLoading) {
        return (
            <LoadingContainer
                sx={{
                    background: 'transparent !important',
                }}
                cover
            />
        );
    }

    return !!order && !!customer ? (
        <Container
            sx={{
                mt: 3,
                ml: 'unset',
            }}
            maxWidth={false}
        >
            <Link
                sx={{
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.primary',
                }}
                href={`/kunden/${customerId}?tab=orders`}
            >
                <SvgIcon
                    fontSize='small'
                    sx={{
                        mr: 2,
                    }}
                >
                    <ArrowLeftIcon />
                </SvgIcon>

                <Typography variant='subtitle2' component='span'>
                    Kunde {customer.firstname} {customer.lastname}
                </Typography>
            </Link>

            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Bestellung von {formatStarjackDate(order?.creationDate)}
            </Typography>
            <Stack spacing={6}>
                <Box>
                    <CustomerOrderCard order={order} customer={customer} />
                </Box>

                <OrderDetailsKeycardsTable
                    order={order}
                    keycards={order?.keycards || []}
                />

                <OrderDetailsShopProductsTable
                    shopProducts={order?.shopProducts || []}
                />

                <OrderDetailsTicketsTable
                    userId={customerId}
                    orderId={orderId}
                />

                <OrderDetailsVouchersTable vouchers={order?.vouchers || []} />

                <OrderDetailsMailsTable orderId={orderId} />
            </Stack>
        </Container>
    ) : null;
};
